import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Camera, Film, Heart, Star, ArrowRight } from "lucide-react";
import axios from "axios";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const heroSlides = [
  {
    url: "https://images.unsplash.com/photo-1744619979713-6133dfcccabe?w=1920&q=85",
    alt: "Cinematic Indian Wedding Couple",
  },
  {
    url: "https://media.godarastudios.com/images/homepage/Kids-landscape-2.webp",
    alt: "Godara Studios Kids Potraits ",
  },
  {
    url: "https://media.godarastudios.com/images/homepage/couple1.webp",
    alt: "Godara Studios Couple Potraits ",
  },
  {
    url: "https://media.godarastudios.com/images/homepage/Kids-landscape-3.webp",
    alt: "Godara Studios Kids Photography",
  },
];

const services = [
  {
    icon: Heart,
    title: "Wedding Photography",
    description: "Capturing the magic of your special day with artistic vision and attention to every precious moment.",
    image: "https://media.godarastudios.com/images/potraits/couple-potrait-1.webp",
  },
  {
    icon: Camera,
    title: "Portrait Sessions",
    description: "Professional portraits that reveal personality, emotion, and authentic beauty.",
    image: "https://media.godarastudios.com/images/potraits/bride-potrait-2.webp",
  },
  {
    icon: Film,
    title: "Cinematic Films",
    description: "Stunning videography that tells your story through cinematic excellence.",
    image: "https://images.unsplash.com/photo-1669050950123-9d6ff9c88e0a?w=800&q=85",
  },
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (Array.isArray(response.data)) {
          setTestimonials(response.data);
        } else {
          console.error("Testimonials API did not return an array:", response.data);
          setTestimonials([]);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
        setTestimonials([]);
      }
    };
    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative h-screen overflow-hidden">
        {/* Slides */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <img
              src={heroSlides[currentSlide].url}
              alt={heroSlides[currentSlide].alt}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>

        {/* Overlay */}
        <div className="absolute inset-0 hero-overlay" />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-gold text-sm uppercase tracking-[0.3em] mb-6"
          >
            Premium Photography Studio
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-serif text-5xl md:text-7xl lg:text-8xl font-medium text-white mb-6 max-w-5xl leading-none"
          >
            Capturing Moments
            <br />
            <span className="text-gradient-gold">That Last Forever</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="text-white/70 text-lg md:text-xl max-w-2xl mb-10"
          >
            Turning your precious moments into timeless art through the lens of passion and creativity.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-book-btn"
              className="bg-gold text-black px-8 py-4 text-sm uppercase tracking-widest font-bold hover:bg-white transition-all duration-500 flex items-center gap-2"
            >
              Book a Shoot
              <ChevronRight size={18} strokeWidth={1.5} />
            </Link>
            <Link
              to="/portfolio"
              data-testid="hero-portfolio-btn"
              className="bg-transparent border border-white/20 text-white px-8 py-4 text-sm uppercase tracking-widest hover:border-gold hover:text-gold transition-all duration-300"
            >
              View Portfolio
            </Link>
          </motion.div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              data-testid={`slide-indicator-${index}`}
              onClick={() => setCurrentSlide(index)}
              className={`w-12 h-1 transition-all duration-500 ${
                index === currentSlide ? "bg-gold" : "bg-white/30"
              }`}
            />
          ))}
        </div>
      </section>

      {/* Intro Section */}
      <section data-testid="intro-section" className="section bg-[#050505]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
                Welcome to Godara Studios
              </span>
              <h2 className="font-serif text-4xl md:text-6xl font-medium text-white mb-8 leading-tight">
                Where Every Frame
                <br />
                Tells a Story
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                With years of experience capturing life's most precious moments, we bring artistic vision, 
                technical excellence, and heartfelt passion to every shoot. Based in India, we serve clients 
                across all major cities, transforming fleeting moments into eternal memories.
              </p>
              <Link
                to="/about"
                data-testid="intro-about-btn"
                className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-widest hover:gap-4 transition-all duration-300"
              >
                Learn Our Story
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1618562227982-baa5e00aca56?w=800&q=85"
                  alt="Studio Shot"
                  className="w-full h-full object-cover img-zoom"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-gold p-8 hidden md:block">
                <p className="text-black font-serif text-5xl font-medium">10+</p>
                <p className="text-black/70 text-sm uppercase tracking-widest">Years Experience</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="section bg-surface">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
              What We Offer
            </span>
            <h2 className="font-serif text-4xl md:text-6xl font-medium text-white">
              Our Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                data-testid={`service-card-${index}`}
                className="service-card group cursor-pointer"
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover service-image"
                  loading="lazy"
                />
                <div className="relative z-10">
                  <service.icon size={32} strokeWidth={1} className="text-gold mb-6" />
                  <h3 className="font-serif text-2xl text-white mb-3">{service.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-widest hover:gap-4 transition-all duration-300"
            >
              View All Services
              <ArrowRight size={18} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="section bg-[#050505]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
              Client Love
            </span>
            <h2 className="font-serif text-4xl md:text-6xl font-medium text-white">
              What They Say
            </h2>
          </motion.div>

          {testimonials.length > 0 && (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="max-w-4xl mx-auto text-center"
              >
                <div className="flex justify-center gap-1 mb-8">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={20}
                      className={i < testimonials[currentTestimonial]?.rating ? "text-gold fill-gold" : "text-white/20"}
                    />
                  ))}
                </div>
                <p
                  data-testid="testimonial-review"
                  className="font-accent text-2xl md:text-3xl text-white/90 italic leading-relaxed mb-8"
                >
                  "{testimonials[currentTestimonial]?.review}"
                </p>
                <div className="flex items-center justify-center gap-4">
                  {testimonials[currentTestimonial]?.image_url && (
                    <img
                      src={testimonials[currentTestimonial].image_url}
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div className="text-left">
                    <p data-testid="testimonial-name" className="text-white font-medium">
                      {testimonials[currentTestimonial]?.name}
                    </p>
                    <p className="text-white/50 text-sm">
                      {testimonials[currentTestimonial]?.event_type} • {testimonials[currentTestimonial]?.location}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {/* Testimonial Indicators */}
          {testimonials.length > 0 && (
            <div className="flex justify-center gap-3 mt-12">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  data-testid={`testimonial-indicator-${index}`}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? "bg-gold w-8" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1606216794079-73f85bbd57d5?w=1920&q=85"
            alt="Wedding Photography"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-4xl md:text-6xl font-medium text-white mb-6">
              Ready to Create
              <br />
              <span className="text-gradient-gold">Timeless Memories?</span>
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">
              Let's discuss your vision and create something beautiful together.
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-block bg-gold text-black px-10 py-5 text-sm uppercase tracking-widest font-bold hover:bg-white transition-all duration-500"
            >
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>

      <WhatsAppButton />
    </main>
  );
}
